import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Search, Trash2, GripVertical, X } from "lucide-react";
import TodoSchedule from "./TodoSchedule";

const CATEGORIES = ["Meeting", "Client Work", "Sales", "Admin", "DeepThree Internal", "Other"];
const CATEGORY_COLORS = {
  Meeting: "#0066ff",
  "Client Work": "#7C3AED",
  Sales: "#10B981",
  Admin: "#F59E0B",
  "DeepThree Internal": "#EC4899",
  Other: "#71717A",
};
const FILTERS = ["today", "all", "completed", "schedule"];

export default function Todos() {
  const [todos, setTodos] = useState([]);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("");
  const [filter, setFilter] = useState("today");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [pingedId, setPingedId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    const { data } = await api.get("/todos");
    setTodos(data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (searchParams.get("new")) { setCreating(true); searchParams.delete("new"); setSearchParams(searchParams); }
  }, [searchParams, setSearchParams]);

  const today = new Date().toISOString().slice(0, 10);
  const filtered = useMemo(() => {
    let list = todos.filter((t) => (!search || t.title.toLowerCase().includes(search.toLowerCase())) && (!cat || t.category === cat));
    if (filter === "today") list = list.filter((t) => t.due_date && t.due_date.slice(0, 10) === today);
    else if (filter === "overdue") list = list.filter((t) => t.due_date && t.due_date.slice(0, 10) < today && !t.completed);
    else if (filter === "completed") list = list.filter((t) => t.completed);
    return list;
  }, [todos, search, cat, filter, today]);

  const toggle = async (t) => {
    setPingedId(t.id);
    setTimeout(() => setPingedId(null), 320);
    setTodos((prev) => prev.map((x) => (x.id === t.id ? { ...x, completed: !x.completed } : x)));
    await api.put(`/todos/${t.id}`, { completed: !t.completed });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/todos/${id}`);
    toast.success("Deleted");
    load();
  };

  const onDragStart = (id) => () => setDraggingId(id);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (overId) => async (e) => {
    e.preventDefault();
    if (!draggingId || draggingId === overId) return;
    const dragIdx = todos.findIndex((t) => t.id === draggingId);
    const overIdx = todos.findIndex((t) => t.id === overId);
    if (dragIdx === -1 || overIdx === -1) return;
    const next = [...todos];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(overIdx, 0, moved);
    setTodos(next);
    setDraggingId(null);
    await api.post("/todos/reorder", { ids: next.map((t) => t.id) });
  };

  return (
    <div data-testid="todos-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Today</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">Daily To-Do</h1>
        </div>
        <button data-testid="todos-new-button" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc] active:scale-[0.98]"><Plus size={16} /> Add To-Do</button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-md border border-zinc-200 bg-white p-1">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`todos-filter-${f}`} onClick={() => setFilter(f)} className={`rounded px-3 py-1 text-xs font-medium capitalize ${filter === f ? "bg-[#E5F0FF] text-[#0066ff]" : "text-zinc-600 hover:bg-zinc-50"}`}>{f === "schedule" ? "Schedule" : f}</button>
          ))}
        </div>
        {filter !== "schedule" && (
          <>
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input data-testid="todos-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search to-dos" className="w-64 rounded-md border border-zinc-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20" />
            </div>
            <select data-testid="todos-filter-category" value={cat} onChange={(e) => setCat(e.target.value)} className="rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm">
              <option value="">All categories</option>
              {CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </>
        )}
      </div>

      {filter === "schedule" ? (
        <div className="mt-6" data-testid="todos-schedule-embed">
          <TodoSchedule embedded />
        </div>
      ) : (
      <div className="mt-6 flex flex-col gap-2">
        {filtered.map((t) => (
          <div
            key={t.id}
            data-testid={`todo-item-${t.id}`}
            draggable
            onDragStart={onDragStart(t.id)}
            onDragOver={onDragOver}
            onDrop={onDrop(t.id)}
            className={`group flex items-center gap-3 rounded-xl border bg-white px-4 py-3 transition-all hover:-translate-y-[1px] hover:shadow-sm ${t.completed ? "border-zinc-100 bg-zinc-50/50 opacity-60" : "border-zinc-200"}`}
          >
            <GripVertical size={14} className="text-zinc-300" />
            <button
              data-testid={`todo-checkbox-${t.id}`}
              onClick={() => toggle(t)}
              className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border transition-all ${t.completed ? "border-[#0066ff] bg-[#0066ff]" : "border-zinc-300 bg-white"} ${pingedId === t.id ? "dt-ping" : ""}`}
            >
              {t.completed && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M5 12l5 5L20 7" /></svg>}
            </button>
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <button data-testid={`todo-edit-${t.id}`} onClick={() => setEditing(t)} className="min-w-0 flex-1 text-left">
                <span className={t.completed ? "todo-complete-strike text-sm" : "text-sm text-zinc-900 hover:text-[#0066ff]"}>{t.title}</span>
              </button>
              <ImportanceDots level={t.importance} />
            </div>
            <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider" style={{ backgroundColor: `${CATEGORY_COLORS[t.category]}1A`, color: CATEGORY_COLORS[t.category] }}>{t.category}</span>
            {t.due_date && <span className="text-xs text-zinc-500">{t.due_date.slice(0, 10)}</span>}
            <button data-testid={`todo-delete-${t.id}`} onClick={() => remove(t.id)} className="rounded p-1.5 text-zinc-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"><Trash2 size={14} /></button>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-zinc-200 p-10 text-center text-sm text-zinc-500">Nothing here. ✨</div>
        )}
      </div>
      )}

      {(creating || editing) && (
        <TodoModal todo={editing} onClose={() => { setCreating(false); setEditing(null); }} onSaved={() => { setCreating(false); setEditing(null); load(); }} />
      )}
    </div>
  );
}

function ImportanceDots({ level }) {
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={`h-1.5 w-1.5 rounded-full ${n <= level ? "bg-[#0066ff]" : "bg-zinc-200"}`} />
      ))}
    </span>
  );
}

function TodoModal({ todo, onClose, onSaved }) {
  const isEdit = Boolean(todo);
  const todayISO = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState(todo || { title: "", notes: "", importance: 3, category: "Other", due_date: todayISO });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    const payload = { ...form };
    // Default to today if user cleared the date
    if (!payload.due_date) payload.due_date = todayISO;
    try {
      if (isEdit) await api.put(`/todos/${todo.id}`, payload);
      else await api.post(`/todos`, payload);
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="todo-modal" className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-900">{isEdit ? "Edit To-Do" : "New To-Do"}</h2>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>
        <div className="mt-6 flex flex-col gap-3">
          <label className="text-sm"><span className="text-xs font-medium text-zinc-600">Title *</span><input data-testid="todo-form-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20" /></label>
          <label className="text-sm"><span className="text-xs font-medium text-zinc-600">Notes</span><textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1.5 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20" /></label>
          <div className="grid grid-cols-3 gap-3">
            <label className="text-sm"><span className="text-xs font-medium text-zinc-600">Importance</span>
              <select data-testid="todo-form-importance" value={form.importance} onChange={(e) => set("importance", parseInt(e.target.value))} className="mt-1.5 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm">
                {[1, 2, 3, 4, 5].map((n) => (<option key={n} value={n}>{n}</option>))}
              </select>
            </label>
            <label className="text-sm"><span className="text-xs font-medium text-zinc-600">Category</span>
              <select data-testid="todo-form-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1.5 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm">
                {CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </label>
            <label className="text-sm"><span className="text-xs font-medium text-zinc-600">Due</span><input type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} className="mt-1.5 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm" /></label>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
          <button data-testid="todo-modal-save" onClick={save} className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc] active:scale-[0.98]">Save</button>
        </div>
      </div>
    </div>
  );
}
